// WbCalibrationsSubpage — Calibration tab body of the weighbridge
// detail page. Shows the current certificate, an "Upload" CTA that
// opens a modal form, and a chronological history table.
//
// NOTE: server contract for POST .../calibrations takes a JSON body with
// a `certificate_url` string (must be a valid URL when provided). It does
// NOT support multipart/form-data — operators paste the URL of a
// pre-uploaded PDF for now. A future phase will wire a true file upload
// via /api/v1/files/upload → URL flow at which point this modal grows a
// file input. Until then we expose the URL field directly.

import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  useCalibrations,
  useCreateCalibration,
  type WbCalibration,
} from '../hooks/useWbWeighbridges';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbCalibrationsSubpage.module.css';

interface FormState {
  certificate_number: string;
  certifier: string;
  calibrated_at: string;
  valid_until: string;
  certificate_url: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  certificate_number: '',
  certifier: '',
  calibrated_at: '',
  valid_until: '',
  certificate_url: '',
  notes: '',
};

function expiryBadge(validUntil: string): JSX.Element {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const v = new Date(validUntil + 'T00:00:00');
  const days = Math.round((v.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (days < 0) return <Badge variant="danger">Expired</Badge>;
  if (days <= 30)
    return <Badge variant="warning">Expires in {days} {days === 1 ? 'day' : 'days'}</Badge>;
  return <Badge variant="success">Valid</Badge>;
}

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface WbCalibrationsSubpageProps {
  weighbridgeEncId: string;
}

export function WbCalibrationsSubpage({
  weighbridgeEncId,
}: WbCalibrationsSubpageProps): JSX.Element {
  const calibrationsQuery = useCalibrations(weighbridgeEncId);
  const createMut = useCreateCalibration(weighbridgeEncId);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  // Newest-first: the server returns calibrations sorted, but resort
  // client-side to keep this independent of any server-side change.
  const certificates = useMemo<WbCalibration[]>(() => {
    const list = calibrationsQuery.data?.calibrations ?? [];
    return [...list].sort((a, b) =>
      b.calibrated_at.localeCompare(a.calibrated_at),
    );
  }, [calibrationsQuery.data]);

  const current = certificates[0] ?? null;

  function openModal() {
    setForm(EMPTY_FORM);
    setError(null);
    setModalOpen(true);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.certificate_number.trim()) {
      setError('Certificate number is required.');
      return;
    }
    if (!form.certifier.trim()) {
      setError('Certifier is required.');
      return;
    }
    if (!form.calibrated_at || !form.valid_until) {
      setError('Both dates are required.');
      return;
    }
    if (form.valid_until <= form.calibrated_at) {
      setError('Valid-until must be after the calibration date.');
      return;
    }
    if (form.certificate_url.trim()) {
      try {
        // Throws TypeError on invalid URL.
        new URL(form.certificate_url.trim());
      } catch {
        setError('Certificate URL must be a valid http(s) URL.');
        return;
      }
    }
    const body = {
      certificate_number: form.certificate_number.trim(),
      certifier: form.certifier.trim(),
      calibrated_at: form.calibrated_at,
      valid_until: form.valid_until,
      certificate_url: form.certificate_url.trim() ? form.certificate_url.trim() : null,
      notes: form.notes.trim() ? form.notes.trim() : null,
    };
    createMut.mutate(body, {
      onSuccess: () => {
        setModalOpen(false);
        setForm(EMPTY_FORM);
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.subpage}>
      <Card
        title="Current Certificate"
        actions={
          <Button variant="primary" icon={Plus} onClick={openModal}>
            Upload New Certificate
          </Button>
        }
      >
        {calibrationsQuery.isLoading ? (
          <div className={styles.empty}>
            <Spinner label="Loading certificates" />
          </div>
        ) : !current ? (
          <p className={styles.empty}>
            No calibration certificates on file. Upload one to record this
            weighbridge as trade-approved.
          </p>
        ) : (
          <div className={styles.currentBlock}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Certificate #</span>
              <span className={styles.fieldValue}>{current.certificate_number}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Certifier</span>
              <span className={styles.fieldValue}>{current.certifier}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Calibrated</span>
              <span className={styles.fieldValue}>{fmtDate(current.calibrated_at)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Valid Until</span>
              <span className={styles.fieldValue}>{fmtDate(current.valid_until)}</span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Status</span>
              <span>{expiryBadge(current.valid_until)}</span>
            </div>
            {current.certificate_url ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Certificate</span>
                <a
                  href={current.certificate_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View PDF
                </a>
              </div>
            ) : null}
          </div>
        )}
      </Card>

      {certificates.length > 0 ? (
        <Card title="History" flush>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Certificate #</th>
                <th>Certifier</th>
                <th>Calibrated</th>
                <th>Expires</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {certificates.map((c) => (
                <tr key={c.enc_id}>
                  <td>{c.certificate_number}</td>
                  <td>{c.certifier}</td>
                  <td>{fmtDate(c.calibrated_at)}</td>
                  <td>{fmtDate(c.valid_until)}</td>
                  <td>{expiryBadge(c.valid_until)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Upload Calibration Certificate"
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={createMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={(e) => submit(e as unknown as React.FormEvent)}
              loading={createMut.isPending}
            >
              Save Certificate
            </Button>
          </>
        }
      >
        <form className={styles.modalForm} onSubmit={submit}>
          <label>
            <span>Certificate Number</span>
            <input
              type="text"
              required
              maxLength={100}
              value={form.certificate_number}
              onChange={(e) =>
                setForm((p) => ({ ...p, certificate_number: e.target.value }))
              }
            />
          </label>
          <label>
            <span>Certifier</span>
            <input
              type="text"
              required
              maxLength={200}
              value={form.certifier}
              onChange={(e) => setForm((p) => ({ ...p, certifier: e.target.value }))}
            />
          </label>
          <label>
            <span>Calibrated Date</span>
            <input
              type="date"
              required
              value={form.calibrated_at}
              onChange={(e) =>
                setForm((p) => ({ ...p, calibrated_at: e.target.value }))
              }
            />
          </label>
          <label>
            <span>Valid Until</span>
            <input
              type="date"
              required
              value={form.valid_until}
              onChange={(e) =>
                setForm((p) => ({ ...p, valid_until: e.target.value }))
              }
            />
          </label>
          <label>
            <span>Certificate URL</span>
            <input
              type="url"
              placeholder="https://… (pre-uploaded PDF)"
              value={form.certificate_url}
              onChange={(e) =>
                setForm((p) => ({ ...p, certificate_url: e.target.value }))
              }
            />
            <span className={styles.hint}>
              File-upload will land in a future phase. For now paste a URL to a
              pre-uploaded PDF (optional).
            </span>
          </label>
          <label>
            <span>Notes</span>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            />
          </label>
          {error ? <p className={styles.error}>{error}</p> : null}
        </form>
      </Modal>
    </div>
  );
}
